import { runConnectionPlan } from "./connection-plan";
import { useOperationIntent } from "./use-operation-intent";
import { singletonPeerRevisions } from "@/lib/utils/connections/singleton-peers";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createConnectionV4,
  deleteConnectionV4,
  replaceConnectionV4,
} from "@/lib/data/requests/connections/connection";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, Connection, MatterInsurance } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  createInsuranceContactConnectionInput,
  getInsuranceContactConnections,
  type SupportedInsuranceContactRole,
} from "@/lib/utils/connections/insurance-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export type InsuranceContactIdsByRole = Partial<
  Record<SupportedInsuranceContactRole, string[]>
>;

type SyncInsuranceConnectionsInput = {
  operationId?: string;
  currentInsurance?: MatterInsurance;
  desiredContactIdsByRole: InsuranceContactIdsByRole;
  insuranceId: string;
  matterId: string;
};

const singleContactRoles = new Set<SupportedInsuranceContactRole>([
  "Insurer",
  "MedPay Adjuster",
  "Property Damage Adjuster",
]);

const roleCommands = (
  insuranceId: string,
  role: SupportedInsuranceContactRole,
  current: Connection[],
  desiredContactIds: string[],
) => {
  const desired = [...new Set(desiredContactIds.filter(Boolean))];
  const desiredSet = new Set(desired);
  const matching = current.filter((connection) =>
    desiredSet.has(connection.targetObjectId),
  );
  const obsolete = current.filter(
    (connection) => !desiredSet.has(connection.targetObjectId),
  );
  const missing = desired.filter(
    (contactId) =>
      !current.some((connection) => connection.targetObjectId === contactId),
  );

  if (singleContactRoles.has(role) && matching.length) return [];
  if (singleContactRoles.has(role) && desired.length > 1)
    throw new Error("Select one Contact for this role.");

  if (
    (singleContactRoles.has(role) &&
      matching.length === 0 &&
      obsolete.length > 0 &&
      missing.length > 0) ||
    (obsolete.length === 1 && missing.length === 1)
  ) {
    const [connectionToReplace] = obsolete;
    // Only same-Contact active contributions are peers. Other Contacts and
    // future bookings remain independent; the backend rejects conflicts.
    const replacement = {
      dateModified: connectionToReplace.dateModified,
      operation: "replace" as const,
      peerRevisions: singletonPeerRevisions(connectionToReplace, current),
      replacement: createInsuranceContactConnectionInput(
        insuranceId,
        missing[0],
        role,
      ),
    };
    return [
      () =>
        replaceConnectionV4(
          connectionToReplace.id,
          replacement,
          connectionToReplace,
        ),
    ];
  }

  if (singleContactRoles.has(role) && obsolete.length && !desired.length) {
    const primary = obsolete[0];
    const operationId = crypto.randomUUID();
    const peers = singletonPeerRevisions(primary, current);
    return [() => deleteConnectionV4(primary, undefined, operationId, peers)];
  }
  return [
    ...obsolete.map((connection) => {
      const operationId = crypto.randomUUID();
      return () => deleteConnectionV4(connection, undefined, operationId);
    }),
    ...missing.map((contactId) => {
      const input = createInsuranceContactConnectionInput(
        insuranceId,
        contactId,
        role,
      );
      return () => createConnectionV4(input);
    }),
  ];
};

export const syncInsuranceContactConnections = async ({
  currentInsurance,
  operationId = crypto.randomUUID(),
  desiredContactIdsByRole,
  insuranceId,
}: SyncInsuranceConnectionsInput) => {
  if (currentInsurance?.connectionsNextCursor)
    throw new Error("Load all owner connections before changing roles.");
  await runConnectionPlan(operationId, () =>
    Object.entries(desiredContactIdsByRole).flatMap(
      ([role, desiredContactIds]) =>
        roleCommands(
          insuranceId,
          role as SupportedInsuranceContactRole,
          getInsuranceContactConnections(
            currentInsurance,
            role as SupportedInsuranceContactRole,
          ),
          desiredContactIds,
        ),
    ),
  );
};

export const useSyncInsuranceContactConnections = () => {
  const queryClient = useQueryClient();
  const intent = useOperationIntent();

  return useMutation({
    ...intent,
    mutationFn: syncInsuranceContactConnections,
    onError: (error: AxiosError<APIError>, variables) => {
      intent.onError(error, variables);
      toastAPIErrorMessage(error, "Error updating insurance connections");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
  });
};
